import { Button, Checkbox } from "antd"
import { API_URL } from "env";
import { useEffect, useState } from "react";

interface PermissionAtribute {
    id: string;
    displayName: string;
    name: string;
    module: string;
    action: string;
    moduleDisplayName: string;
    actionDisplayName: string;
    roles_display: {
        id: string;
        displayName: string;
    }[];
}

interface RoleItem {
    id: string;
    module: {
        id: string;
        name: string;
        action: {
            id: string;
            name: 'read' | 'create' | 'update' | 'delete';
        }[]
    }[];
}



const NewRoleModalContent = ({ onClose }: { onClose: () => void }) => {
    const [roleName, setRoleName] = useState<string>('');
    const [roleDescription, setRoleDescription] = useState<string>('');
    const [checked, setChecked] = useState<boolean>(false);
    const [roleData, setRoleData] = useState<RoleItem[]>([]);
    const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);
    async function fetchPermissions() {
        try {
            const response = await fetch(`${API_URL}api/v1/be/permissions`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
            });
            if (!response.ok) {
                throw new Error('Network response was not ok');
            }
            const data = await response.json();
            const permissions: PermissionAtribute[] = data.data.items.map((item: any) => ({
                id: String(item.id),
                displayName: item.display_name,
                name: item.name,
                module: item.module ?? '', // fallback to empty string if null
                action: item.action,
                moduleDisplayName: item.module_display ?? '', // fallback to empty string if null
                actionDisplayName: item.action_display ?? '', // fallback to empty string if null
                roles_display: Array.isArray(item.roles_display)
                    ? item.roles_display.map((role: any) => ({
                        id: String(role.id),
                        displayName: role.display_name
                    }))
                    : []
            }));
            const grouped = permissions.reduce((acc, perm) => {
                if (!acc[perm.module]) {
                    acc[perm.module] = {
                        id: perm.module,
                        name: perm.moduleDisplayName,
                        action: []
                    };
                }
                acc[perm.module].action.push({
                    id: perm.id,
                    name: perm.action as 'read' | 'create' | 'update' | 'delete'
                });
                return acc;
            }, {} as Record<string, { id: string; name: string; action: { id: string; name: 'read' | 'create' | 'update' | 'delete' }[] }>);

            setRoleData(
                Object.values(grouped)
                    .map(module => ({
                        id: module.id,
                        module: [module]
                    }))
            );
        } catch (error) {
            console.error('Error fetching permissions:', error);
        }
    }
    async function createRole() {
        try {
            const response = await fetch(`${API_URL}api/v1/be/role`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({
                    "name": roleName,
                    "display_name": roleName,
                    "description": roleDescription,
                    "is_active": checked
                })
            });
            if (!response.ok) {
                throw new Error('Failed to create role');
            }
            const data = await response.json();
            const role = data.data;
            const updateRole = await fetch(`${API_URL}api/v1/be/role/assign-permission`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({
                    role_id: role.id,
                    permission_id: selectedPermissions.map(id => Number(id))
                })
            });
            if (!updateRole.ok) {
                throw new Error('Failed to update role permissions');
            }
            setRoleName('');
            setRoleDescription('');
            setChecked(false);
            setSelectedPermissions([]);
            onClose(); // Close the modal after successful creation
        } catch (error) {
            console.error('Error creating role:', error);
        }
    }
    useEffect(() => {
        fetchPermissions();
    }, []);
    return (
        <div className="p-4">
            <h2 className="text-xl font-semibold mb-4">Add New Role</h2>
            <form>
                <div className="mb-4">
                    <label className="block text-sm font-medium mb-2">Role Name</label>
                    <input
                        type="text"
                        className="w-full p-2 border rounded"
                        placeholder="Enter role name"
                        value={roleName}
                        onChange={(e) => setRoleName(e.target.value)}
                    />
                </div>
                <div className="mb-4">
                    <label className="block text-sm font-medium mb-2">Role Description</label>
                    <textarea
                        className="w-full p-2 border rounded"
                        placeholder="Enter role description"
                        value={roleDescription}
                        onChange={(e) => setRoleDescription(e.target.value)}
                        rows={4}
                    />
                </div>
                <div className="mb-4 inline-flex items-center align-middle gap-2">
                    <label className="block text-sm font-medium">Active</label>
                    <Checkbox
                        checked={
                            checked
                        }
                        onChange={(e) => setChecked(e.target.checked)}
                        className="cursor-pointer"
                    />
                </div>
                <div className="mb-4">
                    <div className="flex flex-col gap-2 max-h-72 overflow-y-auto">
                        <label className="block text-sm font-medium mb-2">Assign Permissions</label>
                        {roleData.map((module) => (
                            <div key={module.id} className="border p-4 rounded">
                                <h3 className="font-semibold mb-2">{module.module[0].name}</h3>
                                <div className="flex flex-wrap gap-4">
                                    {module.module[0].action.map((action) => (
                                        <div key={action.id} className="flex items-center gap-2">
                                            <Checkbox
                                                checked={selectedPermissions.includes(action.id)}
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        setSelectedPermissions([...selectedPermissions, action.id]);
                                                    } else {
                                                        setSelectedPermissions(selectedPermissions.filter(id => id !== action.id));
                                                    }
                                                }}
                                            />
                                            <span className="capitalize">{action.name}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
                <Button type="primary" onClick={() => {
                    createRole();
                }}>
                    Save Role
                </Button>
            </form>
        </div>
    )
}
export default NewRoleModalContent;